/**
 * app.tsx — the shell composition and the screen switch.
 *
 * `GET /api/summary` is fetched once per period and feeds the top bar, the banners, the bell and
 * the home screen. The FIRST load renders skeletons; a refresh keeps the previous render at 50%
 * with `aria-busy` (spec-business §2.3 / §4). Everything is gated on the session: without one the
 * router replaces the route with /login and remembers the intended path in `?next=`.
 */

import { useEffect, useState } from 'react';
import type { SummaryResponse } from '../../api-types.ts';
import { ApiError } from './api-select.ts';
import { fmtDate, fmtMoney } from './format.ts';
import { periodLabel } from './dates.ts';
import { he } from './i18n/he.ts';
import { Banner, BottomNav, Sidebar, Topbar, screenTitle } from './components/Shell.tsx';
import { ErrorBox, ScreenSkeleton } from './components/ui.tsx';
import { Icon } from './components/Icon.tsx';
import { AppProvider, useApp } from './hooks/useApp.tsx';
import { cacheKey } from './hooks/cache.ts';
import { useQuery } from './hooks/useQuery.ts';
import { RouterProvider, Link, useRouter } from './hooks/useRouter.tsx';
import { buildPath, type Route } from './router.ts';
import { AlertsScreen } from './screens/Alerts.tsx';
import { CashflowScreen } from './screens/Cashflow.tsx';
import { ExpensesScreen } from './screens/Expenses.tsx';
import { HomeScreen } from './screens/Home.tsx';
import { InvoicesScreen } from './screens/Invoices.tsx';
import { LoansScreen } from './screens/Loans.tsx';
import { LoginScreen } from './screens/Login.tsx';
import { OpsScreen } from './screens/Ops.tsx';
import { SettingsScreen } from './screens/Settings.tsx';
import { VatScreen } from './screens/Vat.tsx';

export function App() {
  return (
    <RouterProvider>
      <AppShellBoot />
    </RouterProvider>
  );
}

function AppShellBoot() {
  const { navigate, route } = useRouter();
  return (
    <AppProvider
      onUnauthenticated={() => {
        if (route.screen !== 'login') navigate(buildPath('login', null, { next: route.path }), { replace: true });
      }}
    >
      <AppShell />
    </AppProvider>
  );
}

/** The desktop subtitle under the screen title (spec §3.1). */
function screenSubtitle(route: Route, summary: SummaryResponse | undefined): string {
  if (!summary) return '';
  const period = periodLabel(summary.period);
  switch (route.screen) {
    case 'home':
    case 'expenses':
      return period;
    case 'cashflow':
      return `${he.nav.cashflow} · ${period}`;
    case 'invoices':
      return `HYP · ${he.invoices.open} ${fmtMoney(summary.collection.openAgorot)} · ${he.invoices.overdue} ${fmtMoney(summary.collection.overdueAgorot)}`;
    case 'vat': {
      const tax = summary.tax;
      if (!tax.period) return he.vat.notConfigured;
      if (tax.period.status === 'paid') return `${he.home.taxSub(tax.period.label, he.vat.basisCash)} · ${he.vat.paid}`;
      return `${he.home.taxSub(tax.period.label, he.vat.basisCash)} · ${he.vat.dueLine(fmtDate(tax.dueDate), tax.daysUntilDue ?? 0)}`;
    }
    case 'loans':
      return he.loans.rankingSub;
    case 'ops':
      return `${he.ops.demoTag} · ${period}`;
    case 'alerts':
      return he.alerts.sub(summary.alerts.unreadCount, summary.alerts.openCount);
    case 'settings':
      return he.settings.sub;
    default:
      return '';
  }
}

function AppShell() {
  const { api, user, setUser, period, syncing, collapsed } = useApp();
  const { route, navigate } = useRouter();
  const [booted, setBooted] = useState(false);

  // restore the session on boot
  useEffect(() => {
    let cancelled = false;
    api.me().then(
      (response) => {
        if (cancelled) return;
        setUser(response.user);
        setBooted(true);
        if (route.screen === 'login') navigate(route.query.next || buildPath('home'), { replace: true });
      },
      (error: unknown) => {
        if (cancelled) return;
        setBooted(true);
        if (error instanceof ApiError && error.code !== 'UNAUTHENTICATED') return;
        if (route.screen !== 'login') navigate(buildPath('login', null, { next: route.path }), { replace: true });
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const summary = useQuery<SummaryResponse>(user ? cacheKey('summary', { period }) : null, () => api.summary(period), {
    scopes: ['summary'],
    enabled: Boolean(user),
  });

  // reset the scroll position when the screen changes
  useEffect(() => {
    document.querySelector('.main')?.scrollTo(0, 0);
  }, [route.screen, route.param]);

  useEffect(() => {
    document.title = `${screenTitle(route.screen)} · ${he.app.name}`;
  }, [route]);

  if (route.screen === 'login') {
    return (
      <div className="app auth">
        <main className="main" id="main-content">
          <div className="content">
            <LoginScreen />
          </div>
        </main>
      </div>
    );
  }

  if (!booted || !user) {
    return (
      <div className="app">
        <main className="main" id="main-content">
          <div className="content">
            <ScreenSkeleton />
          </div>
        </main>
      </div>
    );
  }

  const busy = syncing || summary.busy;
  const firstLoad = summary.loading && !summary.data;

  return (
    <>
      <a className="skip" href="#main-content">
        {he.app.skip}
      </a>
      <div className={`app${collapsed ? ' collapsed' : ''}`}>
        <Sidebar summary={summary.data} />
        <Topbar summary={summary.data} title={screenTitle(route.screen)} subtitle={screenSubtitle(route, summary.data)} />
        <main className="main" id="main-content" tabIndex={-1}>
          <Banner summary={summary.data} />
          <div className={`content${busy ? ' busy' : ''}`} aria-busy={busy || undefined}>
            {firstLoad ? <ScreenSkeleton /> : <Screen route={route} summary={summary.data} error={summary.error} />}
          </div>
        </main>
        <BottomNav summary={summary.data} />
      </div>
    </>
  );
}

function Screen({ route, summary, error }: { route: Route; summary: SummaryResponse | undefined; error: unknown }) {
  if (error && !summary) {
    return (
      <div className="grid">
        <div className="col-12">
          <ErrorBox
            title={he.screenError.title}
            body={he.screenError.body(screenTitle(route.screen))}
            action={
              <Link className="btn sm" to={buildPath('home')}>
                <Icon name="home" className="sm" />
                {he.screenError.home}
              </Link>
            }
          />
        </div>
      </div>
    );
  }
  switch (route.screen) {
    case 'cashflow':
      return <CashflowScreen summary={summary} />;
    case 'invoices':
      return <InvoicesScreen summary={summary} />;
    case 'vat':
      return <VatScreen summary={summary} />;
    case 'expenses':
      return <ExpensesScreen summary={summary} />;
    case 'loans':
      return <LoansScreen summary={summary} />;
    case 'ops':
      return <OpsScreen summary={summary} />;
    case 'settings':
      return <SettingsScreen summary={summary} />;
    case 'alerts':
      return <AlertsScreen summary={summary} />;
    default:
      return summary ? <HomeScreen summary={summary} /> : <ScreenSkeleton />;
  }
}
